const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');

class CashDrawerService {
    /**
     * @param {AgentCashDrawerDriver} agentCashDrawerDriver
     * @param {CurrentDevice} currentDevice
     * @param {LangService} langService
     * @param {ModalFactory} modalFactory
     * @param {PrivilegesManager} privilegesManager
     * @param {ReceiptPrinter} receiptPrinter
     * @param {TradingSessionManager} tradingSessionManager
     * @param {UserService} userService
     * @param {VueModalFactory} vueModalFactory
     */
    constructor(
        agentCashDrawerDriver,
        currentDevice,
        langService,
        modalFactory,
        privilegesManager,
        receiptPrinter,
        tradingSessionManager,
        userService,
        vueModalFactory
    ) {
        this.agentCashDrawerDriver = agentCashDrawerDriver;
        this.currentDevice = currentDevice;
        this.langService = langService;
        this.modalFactory = modalFactory;
        this.privilegesManager = privilegesManager;
        this.receiptPrinter = receiptPrinter;
        this.tradingSessionManager = tradingSessionManager;
        this.userService = userService;
        this.vueModalFactory = vueModalFactory;

        /**
         * @private
         *
         * @type {string|null|undefined}
         */
        this.printerName = undefined;
    }

    /**
     * This method allows us to send a RAW command via ProPoint Agent to a printer with a cash drawer attached so
     * that we can open the cash drawer without having to print.
     *
     * NOTE: At the moment the command is hardcoded below. It appears that this is the command needed to open all the
     * drawers we need to open but if this is not the case we may need to allow users to either choose what command to
     * send (probably by adding another dropdown in the PrintSettingsTab.vue modal or alternatively it may be possible
     * to do this automatically using printer info returned fron the Agent.
     *
     * NOTE: We don't check for PrivilegeNames.OPEN_CASH_DRAWER here because when a printer opens a cash drawer
     * automatically we don't check then and this is supposed to be replacement functionality.
     */
    async openCashDrawerWithoutPrinting() {
        // Return early if the printerName === null. This means we've already checked the printConfig.
        // Allow printerName === undefined to pass, check the privilege and then attempt to parse the printConfig.
        if (this.printerName === null) {
            return;
        }

        try {
            this.getPrinterName();

            if (this.printerName) {
                const command = '1B 70 00 19 FA';
                await this.agentCashDrawerDriver.openCashDrawer(this.printerName, command);
            }
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * @param {string|null|undefined} printerName
     */
    setPrinterName(printerName) {
        this.printerName = printerName;
    }

    /**
     * Extract the name of a printer with an attached cash drawer from saved config.
     *
     * @return {string|null}
     */
    getPrinterName() {
        try {
            if (this.printerName === undefined) {
                const printConfig = JSON.parse(this.currentDevice.device.printConfig);
                const printerName = printConfig?.autoOpenCashDrawerPrinter;

                this.printerName = printerName;
            }
        } catch (e) {
            console.error(e);
        }
        return this.printerName || null;
    }

    /**
     * Open the cash drawer by printing a small "no sale" receipt.
     */
    openCashDrawer() {
        this.privilegesManager
            .requirePrivilege(PrivilegeNames.OPEN_CASH_DRAWER)
            .then(
                /**
                 * @param {PrivilegeCheckResult} result
                 */
                (result) => {
                    if (result.granted) {
                        this.receiptPrinter.printNoSale(
                            this.langService.getText('ADJUST_FLOAT.RECEIPT_OPEN_TITLE')
                        );

                        this.tradingSessionManager.saveEvent(
                            (new TradingSessionEvent(TradingSessionEventType.CASH_DRAWER))
                                .setOperatorID(this.userService.getUserID())
                                .setManagerID(result.grantedBy),
                        );
                    }
                },
            );
    }

    async addToFloat() {
        let privilegeCheckResult;
        try {
            privilegeCheckResult = await this.privilegesManager.requirePrivilege(PrivilegeNames.ADD_CASH_TO_TILL);
        } catch (e) {
            // No permission.
            return;
        }

        this.showAdjustFloatModal('add', privilegeCheckResult.grantedBy)
            .then((floatAdjustment) => {
                if (!floatAdjustment) {
                    // The modal was cancelled.
                    return;
                }

                let receiptTitle = this.langService.getText('ADJUST_FLOAT.RECEIPT_ADD_TITLE');

                if (floatAdjustment?.floatAdjustmentReason?.reason) {
                    receiptTitle += ': ' + floatAdjustment.floatAdjustmentReason.reason;
                }

                // Print receipt with the amount entered.
                if (floatAdjustment) {
                    this.receiptPrinter.printNoSale(
                        receiptTitle,
                        floatAdjustment.amount,
                        floatAdjustment.narrative
                    );
                }
            });
    }

    async removeFromFloat() {
        let privilegeCheckResult;
        try {
            privilegeCheckResult = await this.privilegesManager.requirePrivilege(PrivilegeNames.REMOVE_CASH_FROM_TILL);
        } catch (e) {
            // No permission.
            return;
        }

        // Open the cash drawer by printing a receipt straight away.
        // The receipt includes a blank line to write in the amount taken out.
        this.receiptPrinter.printNoSale(
            this.langService.getText('ADJUST_FLOAT.RECEIPT_REMOVE_TITLE'),
            0
        );

        // Show the float adjustment modal to record the adjustment.
        this.showAdjustFloatModal('remove', privilegeCheckResult.grantedBy);
    }

    /**
     * @private
     *
     * @param {string} mode
     * @param {number} privilegeGrantedByUserID
     *
     * @return {Promise<FloatAdjustment>}
     */
    showAdjustFloatModal(mode, privilegeGrantedByUserID) {
        return this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.ADJUST_FLOAT,
                {
                    mode,
                }
            )
            .then(({ floatAdjustment }) => {
                this.saveFloatAdjustmentTradingSessionEvent(
                    mode === 'add',
                    privilegeGrantedByUserID,
                    floatAdjustment,
                );

                return floatAdjustment;
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    }

    /**
     * @private
     * Save the data returned by the adjustFloat modal.
     *
     * @param {boolean} isAdd
     * @param {number} authUID
     * @param {FloatAdjustment} floatAdjustment
     */
    saveFloatAdjustmentTradingSessionEvent(isAdd, authUID, floatAdjustment) {
        let auditAmount;
        let tradingSessionEvent;

        if (isAdd) {
            auditAmount = floatAdjustment.amount;
            tradingSessionEvent = new TradingSessionEvent(TradingSessionEventType.CASH_IN);
        } else {
            auditAmount = 0 - floatAdjustment.amount;
            tradingSessionEvent = new TradingSessionEvent(TradingSessionEventType.CASH_OUT);
        }

        this.tradingSessionManager.saveEvent(
            tradingSessionEvent
                .setOperatorID(this.userService.getUserID())
                .setManagerID(authUID)
                .setAmount(auditAmount)
                .setFloatAdjustmentReasonID(floatAdjustment.floatAdjustmentReason.ID)
                .setNarrative(floatAdjustment.narrative),
        );
    }
}

module.exports = CashDrawerService;
